import { message } from 'antd';
import * as CryptoJS from 'crypto-js';
import jwt_decode from 'jwt-decode';
import autodeskApi from '../api/AutodeskApi';
import userApi from '../api/UserApi';
import { AutodeskOauthResponse } from '../model/entities';
import cookieService from './CookieService';
import mioAdminSettingService from './MioAdminSettingService';

class AutodeskService {
    private clientId: string | undefined;
    private readonly scope = 'user:read account:read account:write data:read data:write data:search';
    private readonly urlStart = window.location.protocol + '//' + window.location.host;
    private readonly redirectUrl = `${this.urlStart}/autodesk/oauth`;
    private readonly autodeskClientIdText = 'autodeskClientId';
    private readonly autodeskCodeVerifier = 'autodeskCodeVerifier';
    private readonly autodeskAccessTokenText = 'autodeskAccessToken';
    private readonly autodeskAccessRefreshTokenText = 'autodeskAccessRefreshToken';
    private readonly autodeskWindow = 'autodeskWindow';

    /**
     * Initializes the autodesk access token.
     */
    init = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            // initialize the autodesk client id by organization
            this.initClientId();

            // get autodesk authorization code (only when it comes from an autodesk authorization redirection)
            const authorizationCode = await this.getAuthorizationCode();
            accessToken = this.getCachedAccessToken();

            // if authorization code is received, retrieve access and refresh tokens from autodesk (store in cookies)
            if (authorizationCode) {
                accessToken = await this.loadAccessTokenFromAuthorizationCode(authorizationCode);
            }

            // if access token exists but it's not valid, try to refresh it
            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
            // otherwise the access token is valid

            // close autodesk login popup if is opened
            if (localStorage.getItem(this.autodeskWindow)) {
                window.close();
                localStorage.removeItem(this.autodeskWindow);
            }
        } catch (error: any) {
            accessToken = undefined;
            this.removeAccessToken();
            this.removeRefreshAccessToken();
            message.error('Internal error');
        }

        return accessToken;
    };

    /**
     * Returns the autodesk access token  (from cache or via refresh access token)
     * @returns autodesk access token
     */
    getAccessToken = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            accessToken = this.getCachedAccessToken();

            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
        } catch (error: any) {
            accessToken = undefined;
            this.removeAccessToken();
            this.removeRefreshAccessToken();
            message.error('Internal error');
        }

        return accessToken;
    };

    /**
     * Returns the autodesk access token (from cache or via refresh access token, otherwise redirects to login)
     * @returns autodesk access token
     */
    getAccessTokenOrLogin = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            accessToken = this.getCachedAccessToken();

            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
            // if access token doesn't exist redirect to autodesk authorization page
            else if (!accessToken) {
                this.login();
            }
            // otherwise the access token is valid
        } catch (error: any) {
            if (
                error.response &&
                error.response.data &&
                error.response.data.error === 'invalid_grant' &&
                error.response.data.error_description === 'The refresh token is invalid or expired.'
            ) {
                this.login();
            } else if (
                error.response &&
                error.response.data &&
                error.response.data.error === 'invalid_credentials' &&
                error.response.data.error_description === 'The client credentials are invalid.'
            ) {
                this.login();
            } else {
                accessToken = undefined;
                this.removeAccessToken();
                this.removeRefreshAccessToken();
                message.error('Internal error');
            }
        }

        return accessToken;
    };

    logOut = () => {
        this.removeClientId();
        this.removeAccessToken();
        this.removeRefreshAccessToken();
        sessionStorage.removeItem(this.autodeskCodeVerifier);
    };

    /**
     * Return if user is connected to Autodesk
     * @returns a boolean to know if the user is or not connected
     */
    isConnected = (): boolean => {
        const autodeskAccessToken = this.getCachedAccessToken();
        return !!autodeskAccessToken && this.isAccessTokenValid(autodeskAccessToken);
    };

    /**
     * Returns the autodesk client id.
     * @returns the autodesk client id
     */
    getCachedClientId = (): string | undefined => {
        return cookieService.getCookie(this.autodeskClientIdText);
    };

    /**
     * Returns the autodesk access token.
     * @returns the autodesk access token
     */
    getCachedAccessToken = (): string | undefined => {
        return cookieService.getCookie(this.autodeskAccessTokenText);
    };

    /**
     * Returns the autodesk refresh access token.
     * @returns the autodesk refresh access token
     */
    private getCachedRefreshAccessToken = (): string | undefined => {
        return cookieService.getCookie(this.autodeskAccessRefreshTokenText);
    };

    /**
     * Initializes the autodesk client id (property, cookie) using mio admin settings.
     * @returns the autodesk client id
     */
    private initClientId = () => {
        const autodeskSetting = mioAdminSettingService.getAutodeskSetting();

        // load client id
        if (autodeskSetting) {
            this.clientId = autodeskSetting.publicClientId;

            // if new orgnization, reset client id and tokens
            if (this.clientId !== this.getCachedClientId()) {
                this.logOut();
                this.storeClientId(autodeskSetting.publicClientId);
            }
        } else {
            this.clientId = undefined;
            this.logOut();
        }
    };

    private login = () => {
        this.removeAccessToken();
        this.removeRefreshAccessToken();

        // redirect to autodeks authorization page
        const characterArray = CryptoJS.lib.WordArray.random(34).toString();

        // we generate the code challenge for PKCE auth
        const str = CryptoJS.enc.Utf8.parse(characterArray);
        const codeVerifier = CryptoJS.enc.Base64.stringify(str)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
        const hash = CryptoJS.SHA256(codeVerifier);
        const codeChallenge = CryptoJS.enc.Base64.stringify(hash)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');

        // when we have all the data to generate the url, we generate it
        const url = new URL('https://developer.api.autodesk.com/authentication/v2/authorize');
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('client_id', this.clientId!);
        url.searchParams.set('redirect_uri', this.redirectUrl);
        url.searchParams.set('prompt', 'login');
        url.searchParams.set('scope', this.scope);
        url.searchParams.set('code_challenge', codeChallenge);
        url.searchParams.set('code_challenge_method', 'S256');

        // store code verifier in local storage
        sessionStorage.setItem(this.autodeskCodeVerifier, codeVerifier);

        // we create the autodesk login popup
        const width = 600,
            height = 700;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        localStorage.setItem(this.autodeskWindow, 'Autodesk Login');

        window.open(url, 'Autodesk Login', `width=${width},height=${height},top=${top},left=${left}`);
    };

    private getAuthorizationCode = async () => {
        let authorizationCode: string | undefined;
        try {
            const urlObj = new URL(window.location.href);
            if (window.location.pathname === '/autodesk/oauth' && urlObj.searchParams.has('code') && this.clientId) {
                const urlParams = new URLSearchParams(window.location.search);
                authorizationCode = urlParams.get('code') as string;
            }
        } catch (error) {
            console.log(error);
        }

        return authorizationCode;
    };

    private loadAccessTokenFromAuthorizationCode = async (authorizationCode: string): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            const codeVerifier = sessionStorage.getItem(this.autodeskCodeVerifier);
            if (authorizationCode && codeVerifier && this.clientId) {
                const autodeskOauthResponse = await autodeskApi.getToken(
                    authorizationCode,
                    this.clientId,
                    this.redirectUrl,
                    codeVerifier!,
                );
                accessToken = autodeskOauthResponse.access_token;

                // store autodesk access and refresh tokens
                this.storeAccessToken(autodeskOauthResponse);
                this.storeRefreshAccessToken(autodeskOauthResponse);

                // remove code verifier from session storage
                sessionStorage.removeItem(this.autodeskCodeVerifier);

                // send refresh token to api
                await userApi.createSystemUserBim360AccessToken(
                    autodeskOauthResponse.access_token,
                    autodeskOauthResponse.refresh_token,
                );
            }
        } catch (error) {
            console.log(error);
        }

        return accessToken;
    };

    isAccessTokenValid = (accessToken: string | undefined): boolean => {
        let isValid: boolean = false;
        if (accessToken) {
            const tokenDecoded: any = jwt_decode(accessToken);
            const currentTimeInSeconds = Math.floor(Date.now() / 1000);
            isValid = currentTimeInSeconds < tokenDecoded.exp - 300;
        }

        return isValid;
    };

    private refreshAccessToken = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;

        if (this.clientId) {
            const refreshToken = this.getCachedRefreshAccessToken();
            const autodeskOauthResponse = await autodeskApi.refreshToken(this.clientId, refreshToken!, this.scope);

            // store access and refresh tokens in cookies
            this.storeAccessToken(autodeskOauthResponse);
            this.storeRefreshAccessToken(autodeskOauthResponse);

            // send refresh token to api
            await userApi.createSystemUserBim360AccessToken(
                autodeskOauthResponse.access_token,
                autodeskOauthResponse.refresh_token,
            );
            accessToken = autodeskOauthResponse.access_token;
        }

        return accessToken;
    };

    /**
     * Stores the autodesk client id in a cookie.
     * @param autodeskClientId the autodesk client id
     */
    private storeClientId = (autodeskClientId: string) => {
        cookieService.setCookie(this.autodeskClientIdText, autodeskClientId);
    };

    /**
     * Stores the access token in a cookie.
     * @param autodeskOauthResponse the autodesk oauth response
     */
    private storeAccessToken = (autodeskOauthResponse: AutodeskOauthResponse) => {
        cookieService.setCookie(this.autodeskAccessTokenText, autodeskOauthResponse.access_token);
    };

    /**
     * Stores the refresh token in a cookie.
     * @param autodeskOauthResponse the autodesk oauth response
     */
    private storeRefreshAccessToken = (autodeskOauthResponse: AutodeskOauthResponse) => {
        cookieService.setCookie(this.autodeskAccessRefreshTokenText, autodeskOauthResponse.refresh_token);
    };

    /**
     * Removes the client id cookie.
     */
    private removeClientId = () => {
        cookieService.removeCookie(this.autodeskClientIdText);
    };

    /**
     * Removes the access token cookie.
     */
    private removeAccessToken = () => {
        cookieService.removeCookie(this.autodeskAccessTokenText);
    };

    /**
     * Removes the refresh tokencookie.
     */
    private removeRefreshAccessToken = () => {
        cookieService.removeCookie(this.autodeskAccessRefreshTokenText);
    };
}

const autodeskService: AutodeskService = new AutodeskService();
export default autodeskService;
